// Downloads Manager Class
// Handles business logic and state management for the downloads section
// Follows the Manager pattern adapted from MarketplaceManager

import { Manager } from '../../../core';
import { logger } from '../../../core';

export class DownloadsManager extends Manager {
  constructor() {
    super();

    // Core data state
    this.downloads = [];
    this.categories = [];
    this.isLoading = false;

    // UI state
    this.currentView = 'coming-soon'; // 'coming-soon', 'downloads', etc.
  }

  /**
   * Initialize global variables and setup
   */
  async initGlobalVariables() {
    // Navigation manager will be connected via the React hook adapter
  }

  /**
   * Initialize states and data
   */
  async initStates() {
    logger.log({
      tags: "manager|downloads|init",
      color1: "cyan"
    }, "Initializing DownloadsManager states");

    try {
      // For now, just set up basic state
      this.isLoading = false;

      logger.log({
        tags: "manager|downloads|ready",
        color1: "lime"
      }, "DownloadsManager states initialized successfully");
    } catch (error) {
      logger.error({
        tags: "manager|downloads|error",
        color1: "red",
        includeSource: true
      }, "Failed to initialize DownloadsManager states:", error);

      this.isLoading = false;
      throw error;
    }
  }

  /**
   * Set current view mode
   */
  setCurrentView(view) {
    this.currentView = view;
    logger.log({
      tags: "manager|downloads|action",
      color1: "blue"
    }, `View changed to: ${view}`);
  }

  /**
   * Get current view mode
   */
  getCurrentView() {
    return this.currentView;
  }

  /**
   * Build a query string from filter params
   */
  _buildQueryString({ query, sort, tags } = {}) {
    const params = new URLSearchParams();
    if (query && query.trim()) params.set('query', query.trim());
    if (sort) params.set('sort', sort);
    if (tags && tags.length > 0) params.set('tags', tags.join(','));
    const qs = params.toString();
    return qs ? `?${qs}` : '';
  }

  /**
   * Get a single download by ID (public: active only; admin: any)
   */
  async getDownloadById(downloadId) {
    const response = await fetch(`/api/downloads/${downloadId}`, {
      method: 'GET',
      credentials: 'include',
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || 'Failed to fetch download');
    }
    return data.download || null;
  }

  /**
   * Resolve a gated download link through the backend before opening it.
   */
  async resolveDownloadLink(downloadId, linkIndex) {
    const response = await fetch(`/api/downloads/${downloadId}/links/${linkIndex}/resolve`, {
      method: 'GET',
      credentials: 'include',
    });
    const data = await response.json();
    if (!response.ok) {
      const error = new Error(data.error || 'Failed to resolve download link');
      error.requiredAccessType = data.requiredAccessType || null;
      error.action = data.action || null;
      error.actionLabel = data.actionLabel || null;
      error.actionPath = data.actionPath || null;
      error.requiresUpgrade = data.requiresUpgrade === true;
      throw error;
    }
    return data;
  }

  /**
   * Fetch public downloads (active only)
   * Supports query, sort, and tags filter params
   */
  async fetchPublicDownloads({ query, sort, tags } = {}) {
    const qs = this._buildQueryString({ query, sort, tags });
    const response = await fetch(`/api/downloads/public${qs}`, {
      method: 'GET',
      credentials: 'include',
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || 'Failed to fetch downloads');
    }
    return data.downloads || [];
  }

  /**
   * Fetch downloads from API (admin only — includes inactive)
   * Supports query, sort, and tags filter params
   */
  async fetchNewDownloads({ query, sort, tags } = {}) {
    const qs = this._buildQueryString({ query, sort, tags });
    const response = await fetch(`/api/downloads${qs}`, {
      method: 'GET',
      credentials: 'include',
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || 'Failed to fetch downloads');
    }
    return data.downloads || [];
  }

  /**
   * Toggle pinned state of a download (admin only)
   */
  async toggleDownloadPin(downloadId, isPinned) {
    const response = await fetch(`/api/downloads/${downloadId}/pin`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ isPinned }),
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || 'Failed to toggle pin status');
    }
    return data;
  }

  /**
   * Update a download (admin only)
   */
  async updateDownload(downloadId, updateData) {
    const response = await fetch(`/api/downloads/${downloadId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(updateData),
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || 'Failed to update download');
    }
    return data;
  }

  /**
   * Swap pinned order of two downloads (admin only)
   */
  async swapPinnedOrder(downloadId1, pinnedOrder1, downloadId2, pinnedOrder2) {
    const response = await fetch('/api/downloads/swap-pinned-order', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        downloadId1,
        pinnedOrder1,
        downloadId2,
        pinnedOrder2,
      }),
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || 'Failed to swap pinned order');
    }
    return data;
  }

  /**
   * Delete a download (admin only)
   */
  async deleteDownload(downloadId) {
    const response = await fetch(`/api/downloads/${downloadId}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || 'Failed to delete download');
    }
    return data;
  }

  /**
   * Check if tracking is allowed (2-hour cooldown)
   */
  _canTrack(type, downloadId) {
    const key = `download_${type}_${downloadId}`;
    const lastTracked = localStorage.getItem(key);

    if (!lastTracked) return true;

    const lastTrackedTime = parseInt(lastTracked, 10);
    const twoHoursAgo = Date.now() - (2 * 60 * 60 * 1000); // 2 hours in milliseconds

    return lastTrackedTime < twoHoursAgo;
  }

  /**
   * Mark tracking as completed
   */
  _markTracked(type, downloadId) {
    const key = `download_${type}_${downloadId}`;
    localStorage.setItem(key, Date.now().toString());
  }

  /**
   * Track a view for a download (with localStorage deduplication)
   * @returns {Promise<boolean>} true if view was tracked, false if skipped (cooldown) or failed
   */
  async trackView(downloadId) {
    if (!downloadId || !this._canTrack('views', downloadId)) {
      return false; // Skip if already tracked within 2 hours
    }

    try {
      const response = await fetch(`/api/downloads/${downloadId}/track-view`, {
        method: 'POST',
        credentials: 'include',
      });

      if (response.ok) {
        this._markTracked('views', downloadId);
        logger.log({
          tags: "manager|downloads|track",
          color1: "blue"
        }, `Tracked view for download: ${downloadId}`);
        return true;
      }
      return false;
    } catch (error) {
      logger.error({
        tags: "manager|downloads|error",
        color1: "red"
      }, `Failed to track view for download ${downloadId}:`, error);
      return false;
    }
  }

  /**
   * Track a download for a download (with localStorage deduplication)
   * @returns {Promise<boolean>} true if download was tracked, false if skipped (cooldown) or failed
   */
  async trackDownload(downloadId) {
    if (!downloadId || !this._canTrack('downloads', downloadId)) {
      return false; // Skip if already tracked within 2 hours
    }

    try {
      const response = await fetch(`/api/downloads/${downloadId}/track-download`, {
        method: 'POST',
        credentials: 'include',
      });

      if (response.ok) {
        this._markTracked('downloads', downloadId);
        logger.log({
          tags: "manager|downloads|track",
          color1: "green"
        }, `Tracked download for download: ${downloadId}`);
        return true;
      }
      return false;
    } catch (error) {
      logger.error({
        tags: "manager|downloads|error",
        color1: "red"
      }, `Failed to track download for download ${downloadId}:`, error);
      return false;
    }
  }

  /**
   * Clear views for a download (admin only)
   */
  async clearViews(downloadId) {
    const response = await fetch(`/api/downloads/${downloadId}/clear-views`, {
      method: 'POST',
      credentials: 'include',
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || 'Failed to clear views');
    }
    return data;
  }

  /**
   * Clear downloads for a download (admin only)
   */
  async clearDownloads(downloadId) {
    const response = await fetch(`/api/downloads/${downloadId}/clear-downloads`, {
      method: 'POST',
      credentials: 'include',
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || 'Failed to clear downloads');
    }
    return data;
  }

  /**
   * Get available tags (admin only)
   */
  async getTags() {
    const response = await fetch('/api/downloads/tags', {
      method: 'GET',
      credentials: 'include',
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || 'Failed to fetch tags');
    }
    return data.tags || [];
  }

  /**
   * Update available tags (admin only)
   */
  async updateTags(tags) {
    const response = await fetch('/api/downloads/tags', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ tags }),
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || 'Failed to update tags');
    }
    return data.tags || [];
  }
}